// Read-only, same-origin search adapters. No query logging or durable search cache.
export const QUERY_LIMIT = 200;
export const RESULT_LIMIT = 50;
const cooldowns = new Map();

export function normalizeDocumentQuery(value) {
  if (typeof value !== 'string') throw new Error('Enter a search term.');
  const query = value.trim().replace(/\s+/gu, ' ');
  if (!query) throw new Error('Enter a search term.');
  if ([...query].length > QUERY_LIMIT) throw new Error('Use 200 characters or fewer.');
  return query;
}

export function safeDocumentPath(value) {
  if (typeof value !== 'string' || !value || value.length > 2048 || /[\\\u0000-\u001f\u007f?#]/u.test(value)) return null;
  if (value.startsWith('/') || /^[a-z][a-z\d+.-]*:/iu.test(value)) return null;
  const parts = value.split('/');
  if (parts.some(p => !p || p === '.' || p === '..' || p.startsWith('_'))) return null;
  // Reject encoded separators/traversal rather than decoding to a different file.
  if (/%(?:2e|2f|5c|00)/iu.test(value)) return null;
  return value;
}

export function documentIdentity(value) {
  const path = safeDocumentPath(value?.rel_path || value?.path);
  if (!path) return null;
  const name = path.split('/').pop();
  if (!name || name.length > 512 || value?.kind === 'folder') return null;
  return { path, name, folder: path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '' };
}

export function combineDocumentMatches(entries, hits, query) {
  const terms = normalizeDocumentQuery(query).toLocaleLowerCase().split(' ');
  const rows = new Map();
  for (const entry of entries) {
    const doc = documentIdentity(entry);
    if (doc && terms.every(t => doc.name.toLocaleLowerCase().includes(t))) rows.set(doc.path, doc);
  }
  for (const hit of hits) {
    const doc = documentIdentity(hit);
    if (doc) rows.set(doc.path, doc);
  }
  return [...rows.values()].sort((a,b) => a.name.localeCompare(b.name, undefined, {numeric:true}) || a.path.localeCompare(b.path)).slice(0, RESULT_LIMIT);
}

export function searchError(status, retryAfter) {
  const error = new Error(status === 401 ? 'Sign in to search your reports.' : status === 403 ? 'These reports are not available for this account.' : status === 429 ? 'Please wait a moment before searching again.' : 'Search is unavailable. Please try again.');
  error.status = status;
  if (status === 429) {
    const n = retryAfter != null && retryAfter !== '' ? Number(retryAfter) : NaN;
    error.retrySeconds = Number.isFinite(n) && n >= 0 ? Math.min(n, 86400) : 60;
  }
  return error;
}

async function boundedJson(response, maxBytes) {
  if (!response.headers.get('content-type')?.includes('application/json')) throw searchError(502);
  const length = Number(response.headers.get('content-length'));
  if (length > maxBytes) throw searchError(502);
  const reader = response.body?.getReader();
  if (!reader) throw searchError(502);
  const chunks = []; let bytes = 0;
  try {
    while (true) {
      const {done,value} = await reader.read();
      if (done) break;
      bytes += value.byteLength;
      if (bytes > maxBytes) { await reader.cancel(); throw searchError(502); }
      chunks.push(value);
    }
  } finally { reader.releaseLock(); }
  const all = new Uint8Array(bytes); let offset=0;
  for (const chunk of chunks) { all.set(chunk,offset); offset+=chunk.byteLength; }
  try { return JSON.parse(new TextDecoder().decode(all)); } catch { throw searchError(502); }
}

export function validateCatalog(body, listingId) {
  const data = body?.data ?? body;
  if (data?.contractVersion !== 2 || !data.scope?.listingIds?.includes(listingId)) throw searchError(502);
  const block = data.documents?.byListing?.[String(listingId)];
  if (!block || !Array.isArray(block.entries) || block.entries.length > 100000) throw searchError(502);
  if (block.entries.some(e => e.listing_id != null && Number(e.listing_id) !== listingId)) throw searchError(502);
  return block.entries;
}

export function validateContent(body, listingId, query, propertyId) {
  if (body?.outcome !== 'success' || body.schema_version !== 1 || Number(body.listing_id) !== listingId || body.query !== query || !Array.isArray(body.hits) || body.hits.length > 8) throw searchError(502);
  if (propertyId && String(body.property_id) !== String(propertyId)) throw searchError(502);
  if (!['complete','empty','indexing','partial'].includes(body.state)) throw searchError(502);
  for (const hit of body.hits) {
    if (hit.sensitivity !== 'owner_visible' || hit.redaction_status !== 'approved' || (hit.listing_id != null && Number(hit.listing_id) !== listingId)) throw searchError(502);
  }
  // Never expose or retain passages, scan diagnostics, index details or copy state.
  return body.hits.map(h => ({path:h.path}));
}

export async function findOwnerDocuments({listingId, propertyId, query, signal, fetcher=fetch}) {
  if (!Number.isSafeInteger(listingId) || listingId <= 0) throw searchError(403);
  query = normalizeDocumentQuery(query);
  const origin = typeof location === 'undefined' ? 'local-fixture' : location.origin;
  if ((cooldowns.get(origin) || 0) > Date.now()) throw searchError(429, (cooldowns.get(origin)-Date.now())/1000);
  async function request(url, options, maxBytes) {
    const response = await fetcher(url, {credentials:'include',cache:'no-store',signal,...options});
    if (!response.ok) {
      const error = searchError(response.status, response.headers.get('retry-after'));
      if (error.retrySeconds != null) cooldowns.set(origin, Date.now()+error.retrySeconds*1000);
      throw error;
    }
    return boundedJson(response,maxBytes);
  }
  const params = new URLSearchParams({listing_id:String(listingId),domains:'documents'});
  const outcomes = await Promise.allSettled([
    request('/api/v1/owner/sync?'+params, {headers:{'X-Owner-Sync-Contract':'2','Accept':'application/json'}}, 12*1024*1024).then(b=>validateCatalog(b,listingId)),
    request('/api/v1/owner/documents/search', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({listing_id:listingId,query,top_k:8,max_characters_per_hit:300})}, 128*1024).then(b=>validateContent(b,listingId,query,propertyId)),
  ]);
  const denied=outcomes.find(x=>x.status==='rejected'&&[401,403].includes(x.reason?.status));
  if (denied) throw denied.reason;
  if (signal?.aborted) throw new DOMException('Aborted','AbortError');
  const entries=outcomes[0].status==='fulfilled'?outcomes[0].value:[];
  const hits=outcomes[1].status==='fulfilled'?outcomes[1].value:[];
  const documents=combineDocumentMatches(entries,hits,query);
  if (!documents.length && outcomes.some(x=>x.status==='rejected')) throw outcomes.find(x=>x.status==='rejected').reason;
  return documents;
}

export async function openOwnerDocument({listingId, path, signal, fetcher=fetch}) {
  if (!Number.isSafeInteger(listingId) || listingId<=0 || safeDocumentPath(path)!==path) throw searchError(403);
  const params=new URLSearchParams({listing_id:String(listingId),path});
  const response=await fetcher('/api/v1/owner/dropbox/file?'+params,{credentials:'include',signal,cache:'no-store'});
  if (!response.ok) throw new Error([401,403].includes(response.status)?'This document is not available for this account.':'The document could not be opened.');
  const type=(response.headers.get('content-type')||'').split(';')[0].toLowerCase();
  if (!(type==='application/pdf'||/^image\/(png|jpeg|gif|webp)$/u.test(type))) throw new Error('This document cannot be previewed. Open it from its folder.');
  const max=25*1024*1024;
  if (Number(response.headers.get('content-length'))>max) throw new Error('Open this document from its folder.');
  const reader=response.body?.getReader();if(!reader)throw new Error('The document could not be opened.');
  const chunks=[];let size=0;
  try{while(true){const {done,value}=await reader.read();if(done)break;size+=value.byteLength;if(size>max){await reader.cancel();throw new Error('Open this document from its folder.');}chunks.push(value);}}finally{reader.releaseLock();}
  const blob=new Blob(chunks,{type});
  if(type==='application/pdf'&&!((await blob.slice(0,5).text())==='%PDF-'))throw new Error('The document could not be opened.');
  return {blob,type};
}
